import { useEffect, useState } from "react";
import { getLeaderboard } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Loader2 } from "lucide-react";

const FILTERS = [
  { id: null, label: "All" },
  { id: "sdr", label: "SDR Edition" },
  { id: "sales", label: "Enterprise Sales Edition" },
];

const ROLE_LABEL = { sdr: "SDR", sales: "Sales" };

const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <span className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-100 border border-amber-300">
        <Trophy className="w-4 h-4 text-amber-600" />
      </span>
    );
  }
  if (rank === 2 || rank === 3) {
    return (
      <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${rank === 2 ? "bg-slate-100 border-slate-300" : "bg-orange-50 border-orange-200"}`}>
        <Medal className={`w-4 h-4 ${rank === 2 ? "text-slate-500" : "text-orange-500"}`} />
      </span>
    );
  }
  return (
    <span className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-50 border border-slate-200 text-xs font-bold text-slate-500">
      {rank}
    </span>
  );
};

export const LeaderboardCard = () => {
  const [roleFilter, setRoleFilter] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getLeaderboard(roleFilter)
      .then((res) => setEntries(res.data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [roleFilter]);

  return (
    <Card className="border-slate-200" style={{ borderTop: "3px solid #009CDE" }} data-testid="leaderboard-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Team Leaderboard
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">Ranked by average Deal Velocity Index across completed sessions</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button
                key={f.label}
                onClick={() => setRoleFilter(f.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  roleFilter === f.id ? "bg-white text-primary shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`leaderboard-filter-${f.id || "all"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 text-slate-300 animate-spin" />
          </div>
        ) : !entries.length ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="leaderboard-empty">
            No completed sessions from registered learners yet. Finish a simulation while logged in to appear here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                  <th className="py-2 pr-4">Rank</th>
                  <th className="py-2 pr-4">Learner</th>
                  <th className="py-2 pr-4">Editions</th>
                  <th className="py-2 pr-4">Avg DVI</th>
                  <th className="py-2 pr-4">Best DVI</th>
                  <th className="py-2 pr-4">Avg Trust</th>
                  <th className="py-2">Sessions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.rep_id} className={`border-b border-slate-50 ${e.rank <= 3 ? "bg-amber-50/30" : ""}`}
                    data-testid={`leaderboard-row-${e.rep_id}`}>
                    <td className="py-3 pr-4"><RankBadge rank={e.rank} /></td>
                    <td className="py-3 pr-4">
                      <p className="font-medium text-slate-800">{e.name}</p>
                      {e.email && <p className="text-xs text-slate-400">{e.email}</p>}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex gap-1">
                        {e.roles.map((r) => (
                          <Badge key={r} variant="outline" className="text-[10px] border-slate-200 text-slate-500">
                            {ROLE_LABEL[r] || r}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      {e.avg_dvi != null ? (
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900" data-testid={`leaderboard-dvi-${e.rep_id}`}>{e.avg_dvi}</span>
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(100, e.avg_dvi)}%`, background: "#009CDE" }} />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">{e.best_dvi ?? "—"}</td>
                    <td className="py-3 pr-4 text-slate-600">{e.avg_trust}</td>
                    <td className="py-3 text-slate-600">{e.completed_sessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
